// A presentation-only overlay: observes the existing histogram and never writes
// chart data, price-scale options or the viewport. Its text cannot size an axis.
export function attachVolumeReadout({ chart, series, container, paneIndex }) {
  if (!container || !series.subscribeDataChanged) return () => {}
  let pane = null
  const label = document.createElement('div')
  label.dataset.testid = 'volume-readout'
  label.hidden = true
  label.className = 'absolute w-fit z-10 pointer-events-none select-none whitespace-nowrap overflow-hidden rounded bg-canvas/80 px-1 font-mono text-[10px] leading-4 text-inkMuted'
  container.append(label)
  let hoveredIndex = null
  let disposed = false
  let frame = null
  const render = () => {
    if (disposed) return
    // NearestLeft at the upper bound retrieves the latest bar without copying
    // the full dataset on each live update. Full replacements reset hover.
    const bar = hoveredIndex == null
      ? series.dataByIndex(Number.MAX_SAFE_INTEGER, -1)
      : series.dataByIndex(hoveredIndex)
    const value = bar?.value
    label.textContent = `Vol ${Number.isFinite(value) && value >= 0 ? series.priceFormatter().format(value) : '—'}`
    label.setAttribute('aria-label', hoveredIndex == null ? 'Latest candle volume' : 'Hovered candle volume')
  }
  const position = () => {
    if (disposed) return
    const nextPane = chart.panes()[paneIndex]?.getHTMLElement?.()
    // LWC can publish the pane API before creating its DOM on the next paint.
    // Wait for that DOM instead of permanently skipping this readout.
    if (!nextPane) return
    if (nextPane !== pane) {
      pane = nextPane
      observer.observe(pane)
      pendingPaneObserver.disconnect()
    }
    const rect = container.getBoundingClientRect()
    const paneRect = pane.getBoundingClientRect()
    label.hidden = paneRect.height <= 0 || rect.height <= 0
    if (label.hidden) return
    const zoom = container.clientHeight > 0 && rect.height > 0 ? rect.height / container.clientHeight : 1
    label.style.top = `${(paneRect.bottom - rect.top) / zoom - 6}px`
    label.style.transform = 'translateY(-100%)'
    label.style.right = `${chart.priceScale('right').width() + 8}px`
    label.style.maxWidth = `calc(100% - ${chart.priceScale('right').width() + 16}px)`
  }
  const schedulePosition = () => {
    if (disposed || frame != null) return
    frame = requestAnimationFrame(() => { frame = null; position() })
  }
  const onMove = event => {
    hoveredIndex = event.paneIndex === paneIndex && event.point && event.point.x >= 0 && event.point.y >= 0
      && Number.isFinite(event.logical) && event.seriesData?.has(series) ? event.logical : null
    render()
  }
  const onLeave = () => { hoveredIndex = null; render() }
  const onData = type => { if (type === 'full') hoveredIndex = null; render() }
  const observer = new ResizeObserver(schedulePosition)
  const pendingPaneObserver = new MutationObserver(schedulePosition)
  pendingPaneObserver.observe(container, { childList: true, subtree: true })
  observer.observe(container)
  for (const p of chart.panes()) {
    const element = p.getHTMLElement?.()
    if (element) observer.observe(element)
  }
  const timeScale = chart.timeScale()
  timeScale.subscribeSizeChange(schedulePosition)
  chart.subscribeCrosshairMove(onMove)
  series.subscribeDataChanged(onData)
  container.addEventListener('pointerleave', onLeave)
  position()
  render()
  return () => {
    if (disposed) return
    disposed = true
    if (frame != null) cancelAnimationFrame(frame)
    observer.disconnect()
    pendingPaneObserver.disconnect()
    timeScale.unsubscribeSizeChange(schedulePosition)
    chart.unsubscribeCrosshairMove(onMove)
    series.unsubscribeDataChanged(onData)
    container.removeEventListener('pointerleave', onLeave)
    label.remove()
  }
}
